import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaEye,
  FaBullseye,
  FaHeart,
  FaLinkedin,
  FaTwitter,
  FaGithub
} from 'react-icons/fa';
import './AboutPage.css';

const AboutPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const team = [
    {
      name: 'Dilkash Peshimam',
      role: 'Founder & CTO',
      image: 'DP',
      bio: 'Visionary technology leader with over 5 years of hands-on experience in software development and a strong educational background in IT and coding. Known for building and delivering end-to-end software solutions from the ground up, Dilkash has successfully led teams, mentored developers, and driven projects from concept to completion with precision and innovation. As the Founder & CTO, she combines technical depth with strategic thinking—designing scalable architectures, implementing modern engineering practices, and fostering a culture of excellence. With a forward-looking mindset and a passion for solving complex problems, Dilkash continues to push technological boundaries and shape impactful digital solutions.',
      social: {
        linkedin: 'https://www.linkedin.com/in/dilkash-peshimam-80730b1a8/',
        github: 'https://github.com/Dilkashpeshimam18'
      }
    },

  ];

  const values = [
    {
      icon: FaEye,
      title: 'Innovation',
      description: 'Constantly pushing boundaries and embracing new technologies to deliver cutting-edge solutions.'
    },
    {
      icon: FaBullseye,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality in every project, exceeding expectations.'
    },
    {
      icon: FaHeart,
      title: 'Integrity',
      description: 'Building trust through transparency, honesty, and ethical business practices.'
    }
  ];

  const milestones = [
    { year: '2013', event: 'Company Founded', description: 'Started with a vision to transform digital experiences' },
    { year: '2015', event: '50+ Projects', description: 'Reached milestone of 50 successful projects' },
    { year: '2018', event: 'International Expansion', description: 'Opened offices in 3 countries' },
    { year: '2021', event: '100+ Team Members', description: 'Grew to a team of over 100 professionals' },
    { year: '2024', event: 'Industry Leader', description: 'Recognized as top software development agency' }
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>About Us</h1>
            <p>
              We are a team of passionate innovators dedicated to building exceptional digital solutions that drive business growth and transform industries
            </p>
          </motion.div>
        </div>
      </section>

      <section className="about-story" ref={ref}>
        <div className="story-container">
          <motion.div
            className="story-content"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Story</h2>
            <p>
             Dexova emerged from a simple yet powerful vision: to create software solutions that truly make a difference. What started as a small team of passionate developers has grown into a global software development agency serving clients across the world.
            </p>
            <p>
              Over the years, we've helped hundreds of businesses transform their ideas into reality, delivering innovative solutions that drive growth and success. Our commitment to excellence, combined with our passion for technology, has made us a trusted partner for companies of all sizes.
            </p>
            <p>
              Today, we're proud to have a diverse team of experts specializing in various technologies and industries, all united by a common goal: to build software that exceeds expectations and creates lasting value.
            </p>
          </motion.div>

          <motion.div
            className="story-stats"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.div
              className="stat-box-large"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -5, borderColor: 'var(--primary-color)' }}
            >
              <motion.h3
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5, type: 'spring' }}
              >
                30+
              </motion.h3>
              <p>Projects Completed</p>
            </motion.div>
            <motion.div
              className="stat-box-large"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -5, borderColor: 'var(--primary-color)' }}
            >
              <motion.h3
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.6, type: 'spring' }}
              >
                4+
              </motion.h3>
              <p>Years Experience</p>
            </motion.div>
            <motion.div
              className="stat-box-large"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ y: -5, borderColor: 'var(--primary-color)' }}
            >
              <motion.h3
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.7, type: 'spring' }}
              >
                99%
              </motion.h3>
              <p>Client Satisfaction</p>
            </motion.div>
            <motion.div
              className="stat-box-large"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              whileHover={{ y: -5, borderColor: 'var(--primary-color)' }}
            >
              <motion.h3
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.8, type: 'spring' }}
              >
                15+
              </motion.h3>
              <p>Technologies</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="about-values">
        <div className="values-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
              >
                <div className="value-icon">
                  <value.icon />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    

      <section className="about-team">
        <div className="team-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Meet Our Team</h2>
            <p>The talented people behind our success</p>
          </motion.div>

          <div className="team-grid">
            {team.map((member, index) => (
              <motion.div
                key={index}
                className="team-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="team-avatar">{member.image}</div>
                <h3>{member.name}</h3>
                <p className="team-role">{member.role}</p>
                <p className="team-bio">{member.bio}</p>
                <div className="team-social">
                  <a href={member.social.linkedin}><FaLinkedin /></a>
                  <a href={member.social.github}><FaGithub /></a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


    </div>
  );
};

export default AboutPage;
